import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
  Row,
} from "reactstrap"

import axios from "axios"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import TableContainer from "components/Common/TableContainer"
import apiURl from "url"
import * as XLSX from "xlsx"
import excelIcon from "../../../src/assets/images/excel.svg"
const ZoneManager = () => {
  document.title = "Zones"

  const empty = { origin_city: "", destination_city: "", zone: "WC" }
  const [zones, setZones] = useState([])
  const [q, setQ] = useState("")
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(empty)
  const [uploading, setUploading] = useState(false)
  const [duplicateModal, setDuplicateModal] = useState(false)
  const [duplicates, setDuplicates] = useState([])
  const [selectedFile, setSelectedFile] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const fetchZones = async () => {
    try {
      const { data } = await axios.get(`${apiURl}/zones`, { params: { q } })
      if (data.status === "success") setZones(data.data || [])
    } catch (e) {
      console.error(e)
      toastr.error("Failed to load zones")
    }
  }

  useEffect(() => {
    fetchZones()
  }, [q])

  const openAdd = () => {
    setIsEdit(false)
    setEditId(null)
    setForm(empty)
    setModal(true)
  }
  const openEdit = row => {
    setIsEdit(true)
    setEditId(row.id)
    setForm({
      origin_city: row.origin_city,
      destination_city: row.destination_city,
      zone: row.zone,
    })
    setModal(true)
  }

  const save = async e => {
    e.preventDefault()
    if (!form.origin_city || !form.destination_city || !form.zone)
      return toastr.error("All fields are required")
    try {
      console.log(
        "Request URL:",
        isEdit ? `${apiURl}/zones/${editId}` : `${apiURl}/zones`
      )
      console.log("Request Data:", form)
      if (isEdit) {
        const response = await axios.put(`${apiURl}/zones/${editId}`, form)
        console.log("Response:", response.data)
        toastr.success("Updated")
      } else {
        const response = await axios.post(`${apiURl}/zones`, form)
        console.log("Response:", response.data)
        toastr.success("Created")
      }
      setModal(false)
      setForm(empty)
      fetchZones()
    } catch (e) {
      console.error("Error:", e.response?.status, e.response?.data)
      toastr.error(e?.response?.data?.message || `Save failed: ${e.message}`)
    }
  }

  const checkDuplicates = zonesData => {
    const seen = new Set()
    const duplicatesFound = []

    zonesData.forEach((zone, index) => {
      const key = `${zone.origin_city.toLowerCase()}|${zone.destination_city.toLowerCase()}`
      if (seen.has(key)) {
        duplicatesFound.push({
          index: index + 2, // Adding 2 to account for header row and 1-based indexing
          origin_city: zone.origin_city,
          destination_city: zone.destination_city,
          zone: zone.zone,
        })
      } else {
        seen.add(key)
      }
    })

    return duplicatesFound
  }

  const handleFileChange = e => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    } else {
      setSelectedFile(null)
    }
  }

  const handleUpload = () => {
    if (!selectedFile) return toastr.error("Please select a file first")

    const reader = new FileReader()
    reader.onload = evt => {
      const bstr = evt.target.result
      const wb = XLSX.read(bstr, { type: "binary" })
      const wsname = wb.SheetNames[0]
      const ws = wb.Sheets[wsname]
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 })
      const zonesData = []
      for (let i = 1; i < data.length; i++) {
        const row = data[i]
        if (row.length < 3) continue
        const origin_city = row[0]?.toString().trim()
        const destination_city = row[1]?.toString().trim()
        const zone = row[2]?.toString().trim().toUpperCase()
        if (
          origin_city &&
          destination_city &&
          zone &&
          ["WC", "SZ", "DZ", "IN"].includes(zone)
        ) {
          zonesData.push({ origin_city, destination_city, zone })
        }
      }

      if (zonesData.length === 0) return toastr.error("No valid data in file")

      const duplicates = checkDuplicates(zonesData)
      if (duplicates.length > 0) {
        setDuplicates(duplicates)
        setDuplicateModal(true)
        return
      }

      setUploading(true)
      axios
        .post(`${apiURl}/zones/import`, { zones: zonesData })
        .then(res => {
          if (res.data.status === "success") {
            toastr.success(
              `Imported: ${res.data.inserted} created, ${res.data.updated} updated, ${res.data.skipped} skipped`
            )
            fetchZones()
          } else {
            toastr.error(res.data.message || "Import failed")
          }
        })
        .catch(e => {
          toastr.error(e?.response?.data?.message || "Import failed")
        })
        .finally(() => {
          setUploading(false)
          setSelectedFile(null)
          document.getElementById("fileInput").value = ""
        })
    }
    reader.readAsBinaryString(selectedFile)
  }

  const handleDownload = () => {
    const data = [
      ["Origin City", "Destination City", "Zone"],
      ...zones.map(zone => [
        zone.origin_city,
        zone.destination_city,
        zone.zone,
      ]),
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Zones")
    XLSX.writeFile(wb, "zones_export.xlsx")
  }

  const ZONE_LABELS = {
    WC: "WC — Within City",
    SZ: "SZ — Same Zone",
    DZ: "DZ — Different Zone",
    IN: "IN — International",
  }

  const columns = useMemo(
    () => [
      { Header: "Origin City", accessor: "origin_city" },
      { Header: "Destination City", accessor: "destination_city" },
      {
        Header: "Zone",
        accessor: "zone",
        Cell: ({ value }) => ZONE_LABELS[value] ?? value,
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: cell => (
          <div className="d-flex gap-2">
            <Button
              color="info"
              size="sm"
              onClick={() => openEdit(cell.row.original)}
            >
              Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => confirmDelete(cell.value)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  // Modify remove function to use confirmation
  const confirmDelete = id => {
    setDeleteId(id)
    setDeleteModal(true)
  }

  const remove = async () => {
    try {
      await axios.delete(`${apiURl}/zones/${deleteId}`)
      toastr.success("Deleted")
      fetchZones()
    } catch (e) {
      toastr.error("Delete failed")
    } finally {
      setDeleteModal(false)
      setDeleteId(null)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="align-items-center">
          <Col>
            <h5 className="mt-2">Zones</h5>
          </Col>
          <Col className="text-end">
            <div className="d-inline-flex gap-2">
              <Input
                id="fileInput"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                disabled={uploading}
                style={{ maxWidth: 280 }}
              />
              <Button
                color="primary"
                className="btn btn-sm"
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
              >
                <i className="bx bx-upload"></i> Upload
              </Button>

              <Button color="primary" className="btn btn-sm" onClick={openAdd}>
                + Add Zone
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="float-end mt-3">
                  <img
                    src={excelIcon}
                    alt="excel.svg"
                    onClick={handleDownload}
                    disabled={zones.length === 0}
                    style={{
                      cursor: "pointer",
                      border: "1px solid #bfc1c5",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                    id="excelIcon"
                  />
                  <UncontrolledTooltip placement="left" target="excelIcon">
                    Download Data
                  </UncontrolledTooltip>
                </div>
                <TableContainer
                  columns={columns}
                  data={zones}
                  isGlobalFilter={true}
                  customPageSize={10}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={modal} toggle={() => setModal(!modal)} centered>
        <Form onSubmit={save}>
          <ModalHeader toggle={() => setModal(!modal)}>
            {isEdit ? "Edit Zone" : "Add Zone"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Origin City *</Label>
              <Input
                value={form.origin_city}
                onChange={e =>
                  setForm({ ...form, origin_city: e.target.value })
                }
                placeholder="Lahore"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Destination City *</Label>
              <Input
                value={form.destination_city}
                onChange={e =>
                  setForm({ ...form, destination_city: e.target.value })
                }
                placeholder="Islamabad"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Zone *</Label>
              <Input
                type="select"
                value={form.zone}
                onChange={e => setForm({ ...form, zone: e.target.value })}
                required
              >
                <option value="WC">WC — Within City</option>
                <option value="SZ">SZ — Same Zone</option>
                <option value="DZ">DZ — Different Zone</option>
                <option value="IN">IN — International</option>
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              Save
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Modal
        isOpen={duplicateModal}
        toggle={() => setDuplicateModal(!duplicateModal)}
        centered
      >
        <ModalHeader toggle={() => setDuplicateModal(!duplicateModal)}>
          Duplicate Entries Found
        </ModalHeader>
        <ModalBody>
          <p>The following entries in your file are duplicates:</p>
          <ul>
            {duplicates.map((dup, index) => (
              <li key={index}>
                Row {dup.index}: {dup.origin_city} to {dup.destination_city} (
                {ZONE_LABELS[dup.zone] || dup.zone})
              </li>
            ))}
          </ul>
          <p>Please remove duplicates and try uploading again.</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setDuplicateModal(false)
              setSelectedFile(null)
              document.getElementById("fileInput").value = ""
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={deleteModal}
        toggle={() => setDeleteModal(!deleteModal)}
        centered
      >
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete this zone?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={remove}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ZoneManager
